<script setup lang="ts">
import { computed, ref, watch } from "vue";
import LocationSearch from "./LocationSearch.vue";
import FlightDO = Modules.CampaignPlanner.Data.FlightDO;
import SizeDO = Modules.CampaignPlanner.Data.SizeDO;
import CriterionDO = Modules.CampaignPlanner.Data.CriterionDO;
import ScreenDO = Modules.CampaignPlanner.Data.ScreenDO;

const props = defineProps<{
	flight: FlightDO;
	sizes: SizeDO[];
	criteria: CriterionDO[];
	screens: ScreenDO[];
	excludedScreens: ScreenDO[];
}>();

const filteredSizes = computed(() => {
	if (!props.flight.orientation || props.flight.orientation === "All") {
		return props.sizes;
	}
	return props.sizes.filter(
		(s) =>
			!s.orientation ||
			s.orientation.toLowerCase() === props.flight.orientation!.toLowerCase()
	);
});

const filteredCriteria = computed(() => {
	const type = props.flight.is_digital ? "digital" : "static";
	return props.criteria.filter(
		(c) => !c.inventory_type || c.inventory_type.toLowerCase() === type
	);
});

const spreadLabels: Record<number, string> = {
	0: "No spread",
	1: "",
	2: "Medium spread",
	3: "",
	4: "Maximum spread",
};

const categoryMixLabels: Record<number, string> = {
	0: "Road only",
	1: "75/25",
	2: "50/50",
	3: "25/75",
	4: "City only",
};

const multiplesCriteriaSelected = computed(
	() => (props.flight.selected_criteria?.length ?? 0) > 1
);

const averageFillRate = computed(() => {
	const values = [...props.screens, ...props.excludedScreens]
		.map((s) => s.availability?.fill_rate)
		.filter((v): v is number => v != null);
	if (values.length === 0) return null;
	const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
	return Math.round(avg * 100);
});

const periodFillRatePreset = computed(() => {
	return {
		[averageFillRate.value]: `Average fill rate ${averageFillRate.value}%`,
	};
});

const fillRateDraft = ref(props.flight.maximum_fill_rate);
watch(
	() => props.flight.maximum_fill_rate,
	(v) => (fillRateDraft.value = v)
);

const spreadDraft = ref(props.flight.selected_spread_preset);
watch(
	() => props.flight.selected_spread_preset,
	(v) => (spreadDraft.value = v)
);

const categoryMixDraft = ref(props.flight.selected_category_mix_preset);
watch(
	() => props.flight.selected_category_mix_preset,
	(v) => (categoryMixDraft.value = v)
);

const SOV_MIN = 0;
const SOV_MAX = 100;

// SOV is stored as a fraction (0–1) but displayed/edited as an integer percentage (0–100).
const sovModel = (
	location: "road" | "city",
	key: "minimum_sov" | "maximum_sov",
	fallback: number
) =>
	computed<number>({
		get: () => {
			const fraction = props.flight.pqa_sov_limits[location][key];
			return fraction == null ? fallback : Math.round(fraction * 100);
		},
		set: (percent: number) => {
			props.flight.pqa_sov_limits[location][key] = percent / 100;
		},
	});

const roadMinSov = sovModel("road", "minimum_sov", SOV_MIN);
const roadMaxSov = sovModel("road", "maximum_sov", SOV_MAX);
const cityMinSov = sovModel("city", "minimum_sov", SOV_MIN);
const cityMaxSov = sovModel("city", "maximum_sov", SOV_MAX);

const roadSov = computed<[number, number]>({
	get: (): [number, number] => [roadMinSov.value, roadMaxSov.value],
	set: (value: [number, number]) => {
		roadMinSov.value = value[0];
		roadMaxSov.value = value[1];
	},
});

const citySov = computed<[number, number]>({
	get: (): [number, number] => [cityMinSov.value, cityMaxSov.value],
	set: (value: [number, number]) => {
		cityMinSov.value = value[0];
		cityMaxSov.value = value[1];
	},
});
</script>

<template>
	<v-card class="py-5">
		<div class="section-content px-5">
			<!-- Orientation -->
			<div class="field-group">
				<div class="field-label text-caption text-medium-emphasis">
					Orientation
				</div>
				<v-btn-toggle
					v-model="flight.orientation"
					mandatory
					density="compact"
					variant="outlined"
					color="primary"
				>
					<v-btn value="All">All</v-btn>
					<v-btn value="Portrait">Portrait</v-btn>
					<v-btn value="Landscape">Landscape</v-btn>
				</v-btn-toggle>
			</div>

			<!-- Format / Size filter -->
			<div class="field-group">
				<div class="field-label text-caption text-medium-emphasis">Format</div>
				<v-select
					v-model="flight.selected_sizes"
					:items="filteredSizes"
					item-title="label"
					item-value="id"
					variant="filled"
					density="compact"
					hide-details
					multiple
					chips
					closable-chips
					placeholder="All formats"
				/>
			</div>

			<!-- Criteria -->
			<div class="field-group">
				<div class="field-label text-caption text-medium-emphasis">
					Criteria ( Broadsign )
				</div>
				<v-select
					v-model="flight.selected_criteria"
					:items="filteredCriteria"
					item-title="name"
					item-value="id"
					variant="filled"
					density="compact"
					hide-details
					multiple
					chips
					closable-chips
					placeholder="No criteria selected"
				/>
			</div>

			<!-- AND/OR operator (only when 2+ criteria selected) -->
			<div v-if="multiplesCriteriaSelected" class="field-group">
				<div class="field-label text-caption text-medium-emphasis">
					Criteria Logic
				</div>
				<v-btn-toggle
					v-model="flight.use_and_operator_for_selected_criteria"
					mandatory
					density="compact"
					variant="outlined"
					color="primary"
				>
					<v-btn :value="false">OR — any criterion</v-btn>
					<v-btn :value="true">AND — all criteria</v-btn>
				</v-btn-toggle>
			</div>

			<!-- Location Search -->
			<div class="field-group">
				<div class="field-label text-caption text-medium-emphasis">
					Location
				</div>
				<location-search v-model="flight.selected_locations" />
			</div>

			<!-- Fill Rate (digital only) -->
			<div v-if="flight.is_digital && averageFillRate" class="field-group">
				<div class="field-label text-caption text-medium-emphasis">
					Include screens with a fill rate lower than
				</div>
				<div class="d-flex align-center gap-3 px-2">
					<v-slider
						v-model="fillRateDraft"
						@end="flight.maximum_fill_rate = fillRateDraft"
						:min="1"
						:max="100"
						step="1"
						:ticks="periodFillRatePreset"
						color="primary"
						hide-details
						show-ticks="always"
						tick-size="4"
						thumb-label="hover"
					/>
					<span
						class="text-body-2 font-weight-medium pl-3"
						style="width: 55px; text-align: right"
					>
						{{ flight.maximum_fill_rate }}%
					</span>
				</div>
			</div>

			<!-- Spread -->
			<div class="field-group">
				<div class="field-label text-caption text-medium-emphasis">
					Geographical Spread
				</div>
				<div class="d-flex align-center gap-3 px-2">
					<v-slider
						v-model="spreadDraft"
						@end="flight.selected_spread_preset = spreadDraft"
						:min="0"
						:max="4"
						:ticks="spreadLabels"
						show-ticks="always"
						step="1"
						tick-size="4"
						color="primary"
					/>
					<span
						class="text-body-2 font-weight-medium pl-3"
						style="width: 55px; text-align: right"
					>
						&nbsp;
					</span>
				</div>
			</div>

			<div
				v-if="flight.is_digital && flight.type_of_buy === 'goal_impressions'"
				class="field-group"
			>
				<div class="field-label text-caption text-medium-emphasis">
					Share of Voice ( Min -- Max)
				</div>
				<div class="text-caption text-disabled font-italic">
					Filter not visible in map. Processed by the PQA as part of
					optimization.
				</div>

				<!-- Road -->
				<div class="text-caption font-weight-medium pl-2 pt-1">Road</div>
				<div class="d-flex align-center gap-3 px-0">
					<v-range-slider
						v-model="roadSov"
						:max="SOV_MAX"
						:min="SOV_MIN"
						:step="1"
						class="align-center"
						hide-details
					>
						<template v-slot:prepend>
							<v-text-field
								v-model.number="roadMinSov"
								density="compact"
								style="width: 70px"
								type="number"
								variant="filled"
								size="small"
								hide-details
								single-line
							></v-text-field>
						</template>
						<template v-slot:append>
							<v-text-field
								v-model.number="roadMaxSov"
								density="compact"
								style="width: 70px"
								type="number"
								variant="filled"
								size="small"
								hide-details
								single-line
							></v-text-field>
						</template>
					</v-range-slider>
				</div>

				<!-- City -->
				<div class="text-caption font-weight-medium pl-2 pt-1">City</div>
				<div class="d-flex align-center gap-3 px-0">
					<v-range-slider
						v-model="citySov"
						:max="SOV_MAX"
						:min="SOV_MIN"
						:step="1"
						class="align-center"
						hide-details
					>
						<template v-slot:prepend>
							<v-text-field
								v-model.number="cityMinSov"
								density="compact"
								style="width: 70px"
								type="number"
								variant="filled"
								size="small"
								hide-details
								single-line
							></v-text-field>
						</template>
						<template v-slot:append>
							<v-text-field
								v-model.number="cityMaxSov"
								density="compact"
								style="width: 70px"
								type="number"
								variant="filled"
								size="small"
								hide-details
								single-line
							></v-text-field>
						</template>
					</v-range-slider>
				</div>
			</div>
		</div>

		<v-divider v-if="flight.is_digital" class="my-3 mb-4" />

		<div class="section-content px-5">
			<!-- Budget Allocation Road/City (digital only) -->
			<div v-if="flight.is_digital" class="field-group">
				<div
					class="d-flex align-center justify-space-between"
					style="padding-right: 6px"
				>
					<div class="field-label text-caption text-medium-emphasis">
						Allocation Road / City
					</div>
					<v-switch
						v-model="flight.is_category_mix_enabled"
						color="primary"
						density="compact"
						hide-details
						class="mt-0"
					/>
				</div>
				<div v-if="flight.is_category_mix_enabled" class="px-2">
					<v-fade-transition>
						<div
							class="text-caption text-disabled font-italic"
							v-if="![0, 4].includes(flight.selected_category_mix_preset)"
						>
							Filter not visible in map. Processed by the PQA as part of
							optimization unless set to “.. only”.
						</div>
					</v-fade-transition>
					<v-slider
						v-model="categoryMixDraft"
						@end="flight.selected_category_mix_preset = categoryMixDraft"
						:min="0"
						:max="4"
						:ticks="categoryMixLabels"
						show-ticks="always"
						step="1"
						tick-size="4"
						color="primary"
					/>
				</div>
			</div>
		</div>
	</v-card>
</template>

<style scoped lang="scss">
.section-content {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.field-group {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.field-label {
	font-size: 0.75rem;
	letter-spacing: 0.03em;
	text-transform: uppercase;
}

:deep(.v-btn-group .v-btn) {
	font-weight: 400;
	text-transform: none;
	letter-spacing: normal;
}
</style>
